import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackBar } from '../FlowBuilder/Common/SnackBar/index.tsx';

interface UploadModalProps {
  open: boolean;
  onClose: () => void;
  documentTitle: string;
  maxSizeMB?: number;
  onUpload: (file: File) => void;
}

const UploadModal: React.FC<UploadModalProps> = ({
  open,
  onClose,
  documentTitle,
  maxSizeMB = 10,
  onUpload
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { showSnackBar } = useSnackBar();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.size <= maxSizeMB * 1024 * 1024) {
      setSelectedFile(file);
    } else {
      alert('File exceeds maximum size');
    }
  };

  const handleUpload = () => {

    if (selectedFile) {
      onUpload(selectedFile);
      showSnackBar('Upload File Sucessfully.', 'success');
      onClose(); // This will now trigger the useEffect cleanup
    }
  };

  useEffect(() => {
    if (!open) {
      setSelectedFile(null); // Clear file when modal closes
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Upload {documentTitle}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" gutterBottom>
          Please upload your {documentTitle.toLowerCase()}. Make sure the document is clear and all information is visible.
        </Typography>

        <Paper
          variant="outlined"
          sx={{
            mt: 2,
            border: '2px dashed #90caf9',
            p: 4,
            textAlign: 'center',
            cursor: 'pointer',
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            type="file"
            hidden
            ref={fileInputRef}
            onChange={handleFileSelect}
          />
          <Typography>
            {selectedFile ? selectedFile.name : 'Drag & drop your file here, or click to browse'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Max file size: {maxSizeMB}MB
          </Typography>
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleUpload}
          disabled={!selectedFile}
          variant="contained"
        >
          Upload Document
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadModal;
